import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, ShieldCheck, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/access-control`;

const extractError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

// Aba "Bloqueio por IP": só administrador (a página e a API já são restritas ao admin).
const IpBlockTab = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      setData(res.data);
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(extractError(error, 'Erro ao salvar'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    axios
      .get(API)
      .then((res) => {
        setData(res.data);
        // Já deixa o IP desta rede preenchido para o administrador só confirmar
        const { current_ip: currentIp, current_ip_registered: registered } = res.data;
        if (currentIp && !registered) setNewIp(currentIp);
      })
      .catch((error) => toast.error(extractError(error, 'Erro ao carregar o bloqueio por IP')));
  }, []);

  if (!data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const clearForm = () => {
    setNewIp('');
    setNewDescription('');
  };

  const handleAdd = async () => {
    if (!newIp.trim()) {
      toast.error('Informe o IP');
      return;
    }
    const ok = await run(
      () => axios.post(`${API}/ips`, { ip_address: newIp.trim(), description: newDescription.trim() }),
      'IP cadastrado'
    );
    if (ok) clearForm();
  };

  // Um clique: cadastra o IP que o servidor está vendo (o da rede de quem está acessando)
  const handleAddCurrentNetwork = async () => {
    const ok = await run(
      () => axios.post(`${API}/ips`, { ip_address: data.current_ip, description: newDescription.trim() || 'Rede da empresa' }),
      'IP desta rede cadastrado'
    );
    if (ok) clearForm();
  };

  const handleToggle = () =>
    run(
      () => axios.put(`${API}/settings`, { ip_restriction_enabled: !data.ip_restriction_enabled }),
      data.ip_restriction_enabled ? 'Bloqueio por IP desativado' : 'Bloqueio por IP ativado'
    );

  const handleRemove = (entry) => {
    if (!window.confirm(`Remover o IP ${entry.ip_address}?`)) return;
    run(() => axios.delete(`${API}/ips/${entry.id}`), 'IP removido');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <CardTitle>Bloquear acesso fora da empresa</CardTitle>
              </div>
              <CardDescription className="mt-1">
                Cadastre os IPs da empresa e ative o bloqueio. Depois disso, o sistema (painel web e aplicativo
                mobile) só abre a partir desses IPs, inclusive para quem já estava logado. O ponto eletrônico por
                PIN/dispositivo e os links públicos continuam liberados. Com o bloqueio desativado, o acesso é
                liberado de qualquer lugar.
              </CardDescription>
            </div>
            <Button
              type="button"
              onClick={handleToggle}
              disabled={busy}
              variant={data.ip_restriction_enabled ? 'destructive' : 'default'}
              className="whitespace-nowrap"
            >
              {data.ip_restriction_enabled ? 'Desativar bloqueio' : 'Ativar bloqueio'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span
              className={`px-2 py-1 rounded font-medium ${
                data.ip_restriction_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {data.ip_restriction_enabled ? 'Bloqueio ATIVO' : 'Bloqueio desativado'}
            </span>
            <span className="text-gray-600">
              Seu IP atual: <strong className="text-gray-900">{data.current_ip || 'não identificado'}</strong>
            </span>
            {data.current_ip &&
              (data.current_ip_registered ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> IP desta rede já cadastrado
                </span>
              ) : (
                <Button type="button" size="sm" variant="outline" onClick={handleAddCurrentNetwork} disabled={busy}>
                  Cadastrar o IP desta rede
                </Button>
              ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>IPs autorizados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
            <Input
              placeholder="IP, faixa (192.168.1.0/24) ou endereço DDNS (minhaempresa.ddns.net)"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
            />
            <Input
              placeholder="Descrição (ex.: Matriz)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <Button type="button" onClick={handleAdd} disabled={busy}>
              Adicionar
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Sua empresa não tem IP fixo? Cadastre um endereço DDNS (ex.: minhaempresa.ddns.net) configurado no
            roteador. O sistema acompanha o IP atual desse endereço automaticamente, com atualização em até 1 minuto.
          </p>

          {data.allowed_ips.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhum IP cadastrado.</p>
          ) : (
            <div className="divide-y">
              {data.allowed_ips.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-mono text-gray-900">
                      {entry.ip_address}
                      {entry.is_hostname && (
                        <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-sans">DDNS</span>
                      )}
                    </p>
                    {entry.is_hostname &&
                      (entry.resolved_ips.length > 0 ? (
                        <p className="text-sm text-gray-600">IP atual: {entry.resolved_ips.join(', ')}</p>
                      ) : (
                        <p className="text-sm text-yellow-600">Não foi possível localizar este endereço no momento</p>
                      ))}
                    {entry.description && <p className="text-sm text-gray-500">{entry.description}</p>}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleRemove(entry)}
                  >
                    Remover
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IpBlockTab;
